import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import ReactFlow, { ReactFlowProvider, Background, Controls } from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import "react-flow-renderer/dist/theme-default.css";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import VisibilityIcon from "@material-ui/icons/VisibilityOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { nodeTypes } from "../Applications/CustomNodes";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", display: "flex", flexDirection: "column", background: theme.app.chatBg },
  bar: {
    height: 52,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 14px",
    background: "#fff",
    borderBottom: `1px solid ${theme.app.border}`,
  },
  name: { fontWeight: 600, fontSize: 15, color: theme.app.ink },
  badge: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: theme.app.textTertiary, border: `1px solid ${theme.app.border}`, borderRadius: 6, padding: "3px 8px" },
  grow: { flexGrow: 1 },
  deploy: { background: theme.app.dark, color: "#fff", textTransform: "none", fontSize: 13, fontWeight: 500, padding: "7px 16px", boxShadow: "none", "&:hover": { background: "#1f262c", boxShadow: "none" } },
  canvas: { flex: 1, minHeight: 0, position: "relative", "& .react-flow__attribution": { display: "none" } },
}));

const safePositions = flow =>
  (Array.isArray(flow) ? flow : []).map((el, i) =>
    el && el.position ? el : el && el.id ? { ...el, position: { x: 80, y: 80 + i * 90 } } : el
  );

const TemplateView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const { projects, activeProjectId } = useProject();
  const [tpl, setTpl] = useState(null);
  const [elements, setElements] = useState([]);
  const [deploy, setDeploy] = useState(null); // { name, projectId, folderId }
  const [folders, setFolders] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/templates/${id}`);
        setTpl(data);
        setElements(safePositions(data.flow));
      } catch (err) {
        toastError(err);
        history.replace("/templates");
      }
    })();
  }, [id, history]);

  const loadFolders = useCallback(async pid => {
    if (!pid) { setFolders([]); return; }
    try {
      const { data } = await api.get("/folders", { params: { projectId: pid } });
      setFolders(data.folders || data || []);
    } catch (err) {
      setFolders([]);
    }
  }, []);

  const openDeploy = () => {
    const pid = activeProjectId || (projects[0] && projects[0].id) || null;
    setDeploy({ name: tpl ? tpl.name : "", projectId: pid, folderId: "" });
    loadFolders(pid);
  };

  const onProjectChange = pid => {
    setDeploy(d => ({ ...d, projectId: pid, folderId: "" }));
    loadFolders(pid);
  };

  const doDeploy = async () => {
    if (!deploy.name.trim() || !deploy.projectId) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/templates/${id}/deploy`, {
        name: deploy.name.trim(),
        projectId: deploy.projectId,
        folderId: deploy.folderId || null,
      });
      history.push(`/applications/${data.code || data.id}`);
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.bar}>
        <IconButton size="small" onClick={() => history.push("/templates")} title="Voltar">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <span className={classes.name}>{tpl ? tpl.name : "Template"}</span>
        <span className={classes.badge}><VisibilityIcon style={{ fontSize: 15 }} /> Somente visualização</span>
        <div className={classes.grow} />
        <Button className={classes.deploy} variant="contained" disableElevation onClick={openDeploy} disabled={!tpl}>
          Usar este template
        </Button>
      </div>

      <div className={classes.canvas}>
        <ReactFlowProvider>
          <ReactFlow
            elements={elements}
            nodeTypes={nodeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            zoomOnScroll
            onLoad={inst => setTimeout(() => inst.fitView({ padding: 0.2 }), 60)}
            snapToGrid
            snapGrid={[12, 12]}
          >
            <Background variant="lines" gap={20} size={1} color="#ECEAE3" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      <Dialog open={!!deploy} onClose={() => !saving && setDeploy(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Usar template</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
            Vamos criar uma cópia deste fluxo na sua conta. Você pode editá-la depois.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            label="Nome da automação"
            value={deploy ? deploy.name : ""}
            onChange={e => { const v = e.target.value; setDeploy(d => ({ ...d, name: v })); }}
            style={{ marginBottom: 14 }}
          />
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Projeto"
            value={deploy ? deploy.projectId || "" : ""}
            onChange={e => onProjectChange(e.target.value)}
            style={{ marginBottom: 14 }}
          >
            {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Pasta (opcional)"
            value={deploy ? deploy.folderId || "" : ""}
            onChange={e => { const v = e.target.value; setDeploy(d => ({ ...d, folderId: v })); }}
          >
            <MenuItem value="">Sem pasta</MenuItem>
            {folders.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeploy(null)} disabled={saving}>Cancelar</Button>
          <Button onClick={doDeploy} variant="contained" color="primary" disableElevation disabled={saving || !deploy || !deploy.name.trim() || !deploy.projectId}>
            {saving ? "Criando…" : "Criar automação"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TemplateView;
